"use client"

import { useEffect, useState } from "react"
import { useLocale } from "next-intl"
import { useTranslations } from "@/lib/intl/use-translations"
import { AddOnCard } from "@/components/ui/add-on-card"
import { addonDisplayPrice } from "@/components/ui/imaging-addons-section"
import { ProductInfoButton } from "@/components/ui/product-info-modal"
import { ADDON_INFO } from "@/data/product-info"
import { isAvailable } from "@/lib/availability"
import { fetchPrices, type PriceData } from "@/lib/pricing"
import { memberPriceLabelChf } from "@/lib/pricing/member-prices"
import { useCountry } from "@/components/layout/country-provider"
import { useLocalePath } from "@/lib/use-localized-navigation"
import { BLOOD_ADDONS, bloodAddonPath } from "@/data/blood-addons"

/**
 * Blood add-ons — same set rendered on `/pricing` and `/go/bloodtest`.
 * Heading + description are passed in so callers can label the section for
 * their own context. The add-on data (titles, prices, member prices, /tests
 * links) is shared from `@/data/blood-addons`.
 *
 * Prices come from live Stripe data when available, falling back to
 * `FALLBACK_PRICES_CHF` (and "—" on pre-launch EUR markets). When no
 * `initialPrices` is provided the section fetches them client-side, so it
 * works on pages that don't server-load pricing.
 */

interface BloodAddonsSectionProps {
  /** Section heading text. */
  heading: string
  /** Section subtitle / description text. */
  description: string
  /** Optional server-loaded prices; otherwise fetched client-side. */
  initialPrices?: PriceData | null
  /** Inner container max-width utility. Defaults to "max-w-[1440px]". */
  maxWidthClassName?: string
  /** Translated suffix for the green member price (e.g. "for members"). */
  memberPriceLabel?: string
  /** Extra classes on the <section> (e.g. spacing overrides). */
  className?: string
  /** Add-on `titleKey`s to hide — e.g. the add-on whose own detail page this
   *  section is rendered on. */
  excludeTitleKeys?: string[]
}

export function BloodAddonsSection({
  heading,
  description,
  initialPrices = null,
  maxWidthClassName = "max-w-[1440px]",
  memberPriceLabel,
  className,
  excludeTitleKeys,
}: BloodAddonsSectionProps) {
  const countryConfig = useCountry()
  const country = countryConfig.code
  const locale = useLocale()
  const localePath = useLocalePath()
  const t = useTranslations("pricing")
  const [liveP, setLiveP] = useState<PriceData | null>(initialPrices)

  useEffect(() => {
    if (initialPrices) {
      setLiveP(initialPrices)
      return
    }
    fetchPrices(country).then(setLiveP)
  }, [country, initialPrices])

  const visibleBloodAddons = BLOOD_ADDONS.filter(
    (a) =>
      isAvailable(ADDON_INFO[a.titleKey]?.availability, country, locale) &&
      !excludeTitleKeys?.includes(a.titleKey)
  )
  if (visibleBloodAddons.length === 0) return null

  return (
    <section className={className ?? "py-16"}>
      <div className="mx-auto w-full max-w-[1440px] px-4 md:px-10">
        <div className={`mx-auto ${maxWidthClassName}`}>
          <div>
            <h2 className="type-headline-sm text-neutral-900">{heading}</h2>
            <p className="type-body-lg mt-3 max-w-xl text-neutral-600">
              {description}
            </p>
          </div>
          <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2">
            {visibleBloodAddons.map((a) => (
              <div key={a.titleKey}>
                <div className="relative">
                  <AddOnCard
                    title={t(a.titleKey)}
                    description={t(a.descKey)}
                    price={addonDisplayPrice(
                      liveP?.fullPrices[a.titleKey],
                      a.titleKey,
                      countryConfig.code
                    )}
                    memberPrice={
                      countryConfig.currency === "CHF"
                        ? (memberPriceLabelChf(
                            liveP?.fullPrices ?? {},
                            a.titleKey
                          ) ?? undefined)
                        : undefined
                    }
                    memberLabel={memberPriceLabel}
                    imageSrc={a.imageSrc}
                    href={
                      bloodAddonPath(a, country)
                        ? localePath(bloodAddonPath(a, country)!)
                        : undefined
                    }
                  />
                  {ADDON_INFO[a.titleKey] && (
                    <div className="absolute top-3 right-3">
                      <ProductInfoButton
                        data={ADDON_INFO[a.titleKey]}
                        livePrice={liveP?.fullPrices[a.titleKey]}
                      />
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}
